import { useEffect, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

const BLOCKS = [
  {
    id: 0,
    lead: 'Sức mạnh của Máy tính Forex',
    text: 'Bất kỳ chiến lược giao dịch nào thiếu quản trị vốn đều là cờ bạc. Sử dụng công cụ Forex để khóa rủi ro dưới 2%.',
    tag: 'Quản trị vốn',
    value: '-2%',
    delta: '+38%',
  },
  {
    id: 1,
    lead: 'Minh bạch hóa nền tảng',
    text: 'Sử dụng máy tính Forex tính phí Spread để bóc tách mọi chi phí ẩn. Một công cụ Forex tốt sẽ bảo vệ biên lợi nhuận cho chiến lược giao dịch của bạn.',
    tag: 'Phí Spread',
    value: '0.6 pips',
    delta: '+24%',
  },
  {
    id: 2,
    lead: 'Lãi kép dài hạn',
    text: 'Đưa dữ liệu vào máy tính Forex mô phỏng để thiết lập chiến lược giao dịch 5 năm.',
    tag: 'Mô phỏng 5 năm',
    value: '170K',
    delta: '+312%',
  },
];

const BAR_DATA = [
  [
    { x: 'T1', v: 42 },
    { x: 'T2', v: 55 },
    { x: 'T3', v: 48 },
    { x: 'T4', v: 70 },
    { x: 'T5', v: 88 },
    { x: 'T6', v: 102 },
  ],
  [
    { x: 'T1', v: 30 },
    { x: 'T2', v: 38 },
    { x: 'T3', v: 45 },
    { x: 'T4', v: 60 },
    { x: 'T5', v: 74 },
    { x: 'T6', v: 90 },
  ],
  [
    { x: 'T1', v: 25 },
    { x: 'T2', v: 45 },
    { x: 'T3', v: 65 },
    { x: 'T4', v: 95 },
    { x: 'T5', v: 130 },
    { x: 'T6', v: 170 },
  ],
];

const LINE_DATA = [
  [
    { x: 'T1', v: 20 },
    { x: 'T2', v: 34 },
    { x: 'T3', v: 41 },
    { x: 'T4', v: 52 },
    { x: 'T5', v: 68 },
    { x: 'T6', v: 86 },
  ],
  [
    { x: 'T1', v: 15 },
    { x: 'T2', v: 28 },
    { x: 'T3', v: 33 },
    { x: 'T4', v: 47 },
    { x: 'T5', v: 61 },
    { x: 'T6', v: 80 },
  ],
  [
    { x: 'T1', v: 18 },
    { x: 'T2', v: 30 },
    { x: 'T3', v: 44 },
    { x: 'T4', v: 70 },
    { x: 'T5', v: 98 },
    { x: 'T6', v: 132 },
  ],
];

const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid #ede9fe',
  fontSize: 12,
  boxShadow: '0 12px 30px -12px rgba(124,58,237,0.25)',
};

const gridStroke = '#e2e8f0';

export default function ScrollVisualization() {
  const [active, setActive] = useState(0);
  const blockRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const autoScroll = useRef(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && autoScroll.current) {
            const idx = Number(entry.target.getAttribute('data-index'));
            setActive(idx);
          }
        });
      },
      { threshold: 0.6 },
    );

    blockRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSelect = (i: number) => {
    autoScroll.current = false;
    setActive(i);
  };

  const renderLineDot = (props: { cx?: number; cy?: number; index?: number }) => {
    const { cx, cy, index } = props;
    if (cx == null || cy == null || index == null) return <g />;
    const isLast = index === LINE_DATA[active].length - 1;
    if (!isLast) return <g />;
    return (
      <g>
        <circle cx={cx} cy={cy} r={7} fill="#7c3aed" opacity={0.22} className="animate-pulse" />
        <circle cx={cx} cy={cy} r={4.5} fill="#7c3aed" stroke="#fff" strokeWidth={2} />
      </g>
    );
  };

  return (
    <section id="visualization" className="relative bg-slate-50 py-20 md:py-28 px-4 md:px-6">
      <div className="mx-auto max-w-[1180px]">
        {/* Section header */}
        <div className="mb-12 flex flex-col gap-2 md:mb-16">
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">
            Trực quan hóa dữ liệu
          </span>
          <h2 className="font-display text-2xl font-bold text-slate-900 md:text-4xl">
            Dữ liệu thay đổi theo từng chiến lược
          </h2>
        </div>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Left: compact vertical stepper */}
          <div className="relative flex flex-col">
            <div className="absolute left-[19px] top-6 bottom-6 w-px bg-slate-200" />
            {BLOCKS.map((block, i) => {
              const isActive = active === i;
              return (
                <button
                  key={block.id}
                  ref={(el) => {
                    blockRefs.current[i] = el;
                  }}
                  data-index={i}
                  type="button"
                  onClick={() => handleSelect(i)}
                  className="group relative flex gap-5 py-6 text-left transition-all duration-300"
                >
                  {/* Node */}
                  <span
                    className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                      isActive
                        ? 'border-violet-600 bg-violet-600 text-white shadow-[0_0_0_4px_rgba(124,58,237,0.10)]'
                        : 'border-slate-200 bg-slate-100 text-slate-400 group-hover:border-violet-400 group-hover:text-violet-500'
                    }`}
                  >
                    <span className="font-mono text-xs font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </span>

                  <div className="min-w-0 pt-0.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                          isActive
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {block.tag}
                      </span>
                    </div>
                    <h3
                      className={`mt-2 font-display text-xl md:text-2xl ${
                        isActive ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'
                      }`}
                    >
                      {block.lead}
                    </h3>
                    <p
                      className={`mt-2 max-w-md text-sm leading-relaxed md:text-base ${
                        isActive ? 'text-slate-600' : 'text-slate-500'
                      }`}
                    >
                      {block.text}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: sticky overlapping UI composition */}
          <div className="relative">
            <div className="sticky top-28">
              <div className="relative h-[520px]">
                {/* Back card — Bar Chart (dimmed / pushed to background) */}
                <div
                  className="absolute bottom-0 left-0 w-[76%] rounded-3xl border border-white/60 bg-white/70 p-5 opacity-40 blur-[2px] transition-all duration-700 ease-out"
                  style={{ transform: `translateY(${active * -8}px) scale(0.95)` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">
                      Phân bổ vốn
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-violet-700">
                      <span className="h-2 w-2 rounded-sm bg-violet-600"></span>
                      Điện tím
                    </span>
                  </div>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-display text-2xl font-bold text-slate-900">
                      {BLOCKS[active].value}
                    </span>
                    <span className="mb-1 text-xs font-semibold text-emerald-600">
                      {BLOCKS[active].delta}
                    </span>
                  </div>
                  <div className="mt-2 h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={BAR_DATA[active]} margin={{ top: 10, right: 0, left: -24, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 6" vertical={false} stroke={gridStroke} />
                        <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="v" fill="#7C3AED" radius={[6, 6, 0, 0]} barSize={22} opacity={0.9} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Front card — Line Chart (active glassmorphism) */}
                <div
                  className="absolute right-0 top-0 w-[82%] rounded-3xl border border-white/60 bg-white/75 p-5 backdrop-blur-md shadow-xl transition-all duration-700 ease-out"
                  style={{ transform: `translateY(${active * 6}px)` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 rounded-full bg-violet-100/70 px-3 py-1 text-xs font-semibold text-violet-700">
                      <i className="ri-live-line text-xs"></i>
                      Live
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-indigo-500">
                      <span className="h-2 w-2 rounded-sm bg-indigo-400"></span>
                      Xu hướng
                    </span>
                  </div>
                  <div className="mt-3">
                    <span className="font-display text-lg font-bold text-slate-900">
                      {BLOCKS[active].lead}
                    </span>
                  </div>
                  <div className="mt-3 h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={LINE_DATA[active]} margin={{ top: 10, right: 0, left: -24, bottom: 0 }}>
                        <defs>
                          <linearGradient id="lineStrokeGrad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#a78bfa" />
                            <stop offset="100%" stopColor="#7c3aed" />
                          </linearGradient>
                          <linearGradient id="lineAreaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.22} />
                            <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 6" vertical={false} stroke={gridStroke} />
                        <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Area
                          type="monotone"
                          dataKey="v"
                          stroke="none"
                          fill="url(#lineAreaGrad)"
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="v"
                          stroke="url(#lineStrokeGrad)"
                          strokeWidth={3}
                          dot={renderLineDot}
                          activeDot={{ r: 5, fill: '#7C3AED', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}